/**
 * Enumerations for the application
 *
 * IMPORTANT: These enums match the backend Prisma schema and service enums exactly.
 * Using const enums for compile-time and runtime type safety.
 */

// ============================================
// BASIC ENUMS
// ============================================

// Basic status enum (frontend-only)
export enum BasicStatus {
  DISABLE = 0,
  ENABLE = 1,
}

// API result status (frontend-only)
export enum ResultStatus {
  SUCCESS = 0,
  ERROR = -1,
  TIMEOUT = 401,
}

// ============================================
// PRISMA SCHEMA ENUMS
// ============================================

/**
 * User roles
 * Matches backend Prisma enum: UserRole
 */
export enum UserRole {
  ADMIN = 'ADMIN',
  ACCOUNTANT = 'ACCOUNTANT',
}

/**
 * Transaction types
 * Matches backend Prisma enum: TransactionType
 */
export enum TransactionType {
  INCOME = 'INCOME',
  EXPENSE = 'EXPENSE',
}

/**
 * Payment methods
 * Matches backend Prisma enum: PaymentMethod
 */
export enum PaymentMethod {
  CASH = 'CASH',
  MASTER = 'MASTER',
}

/**
 * Currency types
 * NOTE: Currency enum has been REMOVED from frontend enums.
 * Currency is now managed via global settings (CurrencySettings).
 * The Currency enum still exists in Prisma schema for historical data integrity.
 * For type-safety, use the Currency type from backend-generated types (#/entity).
 */

/**
 * Contact types
 * Matches backend Prisma enum: ContactType
 */
export enum ContactType {
  SUPPLIER = 'SUPPLIER',
  CUSTOMER = 'CUSTOMER',
  BOTH = 'BOTH',
  OTHER = 'OTHER',
}

/**
 * Payable status (Accounts Payable - money we owe)
 * Matches backend Prisma enum: PayableStatus
 */
export enum PayableStatus {
  PENDING = 'PENDING',
  PARTIAL = 'PARTIAL',
  PAID = 'PAID',
}

/**
 * Receivable status (Accounts Receivable - money owed to us)
 * Matches backend Prisma enum: ReceivableStatus
 */
export enum ReceivableStatus {
  PENDING = 'PENDING',
  PARTIAL = 'PARTIAL',
  PAID = 'PAID',
}

/**
 * Debt status (DEPRECATED - use PayableStatus/ReceivableStatus)
 * Matches backend Prisma enum: DebtStatus
 */
export enum DebtStatus {
  ACTIVE = 'ACTIVE',
  PAID = 'PAID',
  PARTIAL = 'PARTIAL',
}

/**
 * Inventory units
 * Matches backend Prisma enum: InventoryUnit
 */
export enum InventoryUnit {
  KG = 'KG',
  PIECE = 'PIECE',
  LITER = 'LITER',
  OTHER = 'OTHER',
}

/**
 * Inventory operation types
 * Matches backend Prisma enum: InventoryOperationType
 */
export enum InventoryOperationType {
  PURCHASE = 'PURCHASE',
  CONSUMPTION = 'CONSUMPTION',
}

/**
 * Linked entity types for transaction categories
 * Determines which fields to show in transaction form
 */
export enum LinkedEntity {
  EMPLOYEE = 'EMPLOYEE',
  PAYABLE = 'PAYABLE',
  RECEIVABLE = 'RECEIVABLE',
  INVENTORY = 'INVENTORY',
}

/**
 * Discount types
 * Matches backend Prisma enum: DiscountType
 */
export enum DiscountType {
  PERCENTAGE = 'PERCENTAGE',
  AMOUNT = 'AMOUNT',
}

/**
 * Transaction modification types
 * Matches backend enum: TransactionModificationType
 */
export enum TransactionModificationType {
  DEBT_PAYMENT = 'DEBT_PAYMENT',
  DEBT_COLLECTION = 'DEBT_COLLECTION',
  MANUAL_UPDATE = 'MANUAL_UPDATE',
}

/**
 * Employee status
 * Matches backend Prisma enum: EmployeeStatus
 */
export enum EmployeeStatus {
  ACTIVE = 'ACTIVE',
  RESIGNED = 'RESIGNED',
}

/**
 * Employee adjustment types
 * Matches backend Prisma enum: EmployeeAdjustmentType
 */
export enum EmployeeAdjustmentType {
  BONUS = 'BONUS',
  DEDUCTION = 'DEDUCTION',
  ADVANCE = 'ADVANCE',
}

/**
 * Employee adjustment status
 * Matches backend Prisma enum: EmployeeAdjustmentStatus
 */
export enum EmployeeAdjustmentStatus {
  PENDING = 'PENDING',
  PROCESSED = 'PROCESSED',
  CANCELLED = 'CANCELLED',
}

/**
 * Notification severity levels
 * Matches backend Prisma enum: NotificationSeverity
 */
export enum NotificationSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  CRITICAL = 'CRITICAL',
}

/**
 * Notification display methods
 * Matches backend Prisma enum: DisplayMethod
 */
export enum DisplayMethod {
  POPUP = 'POPUP',
  TOAST = 'TOAST',
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

/**
 * Report types
 * Matches backend Prisma enum: ReportType
 */
export enum ReportType {
  FINANCIAL = 'FINANCIAL',
  DEBTS = 'DEBTS',
  INVENTORY = 'INVENTORY',
  SALARY = 'SALARY',
  BRANCHES = 'BRANCHES',
  CUSTOM = 'CUSTOM',
}

// ============================================
// BACKEND SERVICE ENUMS
// ============================================

/**
 * Notification types
 * Matches notification types used in backend services
 * These are string literals used in the notification.type field
 */
export enum NotificationType {
  OVERDUE_DEBT = 'overdue_debt',
  NEW_DEBT = 'new_debt',
  DEBT_PAYMENT = 'debt_payment',
  DEBT_PAID = 'debt_paid',
  LARGE_TRANSACTION = 'large_transaction',
  BACKUP_REMINDER = 'backup_reminder',
}

/**
 * Audit log actions
 * Matches backend enum: AuditAction
 * From: backend/src/common/audit-log/audit-log.service.ts
 */
export enum AuditAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  VIEW = 'VIEW',
}

/**
 * Audit log entity types
 * Matches cloudflare-api enum: AuditEntityType
 */
export enum AuditEntityType {
  TRANSACTION = 'TRANSACTION',
  BRANCH = 'BRANCH',
  USER = 'USER',
  INVENTORY = 'INVENTORY',
  INVENTORY_ITEM = 'INVENTORY_ITEM',
  EMPLOYEE = 'EMPLOYEE',
  EMPLOYEE_ADJUSTMENT = 'EMPLOYEE_ADJUSTMENT',
  SALARY_PAYMENT = 'SALARY_PAYMENT',
  CONTACT = 'CONTACT',
  PAYABLE = 'PAYABLE',
  PAYABLE_PAYMENT = 'PAYABLE_PAYMENT',
  RECEIVABLE = 'RECEIVABLE',
  RECEIVABLE_PAYMENT = 'RECEIVABLE_PAYMENT',
  CATEGORY = 'CATEGORY',
  COMPOSITE_ITEM = 'COMPOSITE_ITEM',
  SETTINGS = 'SETTINGS',
}

// ============================================
// THEME ENUMS (re-exported)
// ============================================

// Theme enums (re-exported from theme/type.ts)
export { ThemeMode, ThemeColorPresets, ThemeLayout } from '../theme/type';
export type { Direction } from '../theme/type';

/**
 * Currency codes
 */
export enum Currency {
  IQD = 'IQD',
  USD = 'USD',
}
